import BoundingBox from "App/utils/bounding_box";
import {mat4, vec3, vec4} from "gl-matrix";
import {FrustumVolume} from "App/utils/view_frustum";
import {project} from "App/utils/glu";

const ctx: Worker = self as any;

export default
class ResolutionComputeWorker {
    private _textureDataHandlers: any = {};
    private _currentRecomputeLODMessage: any;
    private _recomputeLODScheduled: boolean;
    private _viewFrustmCulling: boolean;
    private _textureResolutionLimit: number;

    constructor() {
        this._textureDataHandlers = {};

        this._currentRecomputeLODMessage = null;
        this._recomputeLODScheduled = false;

        this._viewFrustmCulling = false;
        this._textureResolutionLimit = 65535;

        setInterval(this._recomputeTextureLOD.bind(this), 500);
    }

    public addTextureDataHandler(aMessage: any) {
        let textureDataHandler = this._textureDataHandlers[aMessage.UID];
        if (textureDataHandler === undefined) {
            textureDataHandler = {
                UID: aMessage.UID,
                widths: aMessage.widths, // an array of values <1..n)
                heights: aMessage.heights,
                bboxes: {}
            };

            this._textureDataHandlers[aMessage.UID] = textureDataHandler;
        }
    }

    public removeTextureDataHandler(aTextureDataHandler: any) {
        let textureDataHandler = this._textureDataHandlers[aTextureDataHandler.UID];
        if (textureDataHandler != undefined) {
            delete this._textureDataHandlers[aTextureDataHandler.UID];
        }
    };

    public attachShapeBBox(aMessage) {
        let textureDataHandler = this._textureDataHandlers[aMessage.UID];
        if (textureDataHandler != undefined) {
            textureDataHandler.bboxes[aMessage.shapeUID] = new BoundingBox(aMessage.bboxMin, aMessage.bboxMax);
        }
    }

    public updateShapeBBox(aMessage: any) {
        let textureDataHandler = this._textureDataHandlers[aMessage.UID];
        if (textureDataHandler != undefined) {
            let bbox = textureDataHandler.bboxes[aMessage.shapeUID];
            if (bbox !== undefined) {
                bbox.min = aMessage.bboxMin;
                bbox.max = aMessage.bboxMax;
            }
        }
    }

    public detachShapeBBox(aMessage: any) {
        let textureDataHandler = this._textureDataHandlers[aMessage.UID];
        if (textureDataHandler != undefined) {
            let bbox = textureDataHandler.bboxes[aMessage.shapeUID];
            if (bbox !== undefined) {
                delete textureDataHandler.bboxes[aMessage.shapeUID];
            }
        }
    }

    private _computeLevel(aState: any, aTextureDataHandler: any) {
        aState.level = 0;
        for (let i = 0; i < aTextureDataHandler.widths.length; i++) {
            if (Math.min(aState.projectedBBoxResolution, this._textureResolutionLimit) >= Math.max(aTextureDataHandler.widths[i], aTextureDataHandler.heights[i])) {
                aState.level = i;
            } else {
                break;
            }
        }

        // for texture lods ignore very switching to low resolution as it is almost always intended for download purposes
        if (aTextureDataHandler.widths.length > 1 && aTextureDataHandler.widths[0] < 128) {
            aState.level = Math.max(aState.level, 1);
        }
    };

    public scheduleRecomputeTextureLOD() {
        this._recomputeLODScheduled = true;
    };

    private _recomputeTextureLOD() {
        if (!this._recomputeLODScheduled) {
            return;
        }

        this._recomputeLODScheduled = false;

        if (!this._currentRecomputeLODMessage) {
            ctx.postMessage({type: 'recomputed', computedValues: []});
            return;
        }

        let eye = this._currentRecomputeLODMessage.eyes[0];
        let target = this._currentRecomputeLODMessage.targets[0];
        let projMatrix = this._currentRecomputeLODMessage.projMatrixes[0];
        let vpSize = this._currentRecomputeLODMessage.vpSizes[0];

        let viewMatrix = mat4.create();
        mat4.lookAt(viewMatrix, eye, target, vec3.fromValues(0.0, 1.0, 0.0));

        let mvp;
        let inputMVP = mat4.create();
        mat4.multiply(inputMVP, projMatrix, viewMatrix);

        let lookAtBBoxMVP = mat4.create();

        // create view frustum for current model view projection matrix
        let viewFrustum = new FrustumVolume(inputMVP);

        let computedStates = [];

        // pass through all shapes and update the computation fields stored in referenced texture for each shape
        let diffVec = vec3.create();
        let inverseMvp = mat4.create();
        let right = vec4.fromValues(1, 0, 0, 0);
        let resolutionScaleFactor;
        let textureDataHandler;
        let state;
        let closestPointOnBbox;
        for (let key in this._textureDataHandlers) {

            textureDataHandler = this._textureDataHandlers[key];

            state = {
                UID: key,
                projectedBBoxResolution: 0,
                level: 0,
                visible: false,
                distance: 9007199254740992 // max int number
            };

            let bbox;
            let bboxCulled;
            for (let key in textureDataHandler.bboxes) {
                bbox = textureDataHandler.bboxes[key];

                bboxCulled = viewFrustum.intersect(bbox, 0) === -1;

                if (bboxCulled) {
                    bboxCulled = true; // shape is always visible

                    // compute distance eye and intersection point on plane which is perpendicular to view direction and closest point on bounding box lies on that plane
                    closestPointOnBbox = bbox.getClosestPoint(vec3.fromValues(eye[0], eye[1], eye[2]));

                    // recompute the mvp matrix so that it target to the shape's bbox center
                    mat4.lookAt(viewMatrix, eye, closestPointOnBbox, vec3.fromValues(0.0, 1.0, 0.0));
                    mat4.multiply(lookAtBBoxMVP, projMatrix, viewMatrix);

                    mvp = lookAtBBoxMVP;

                    resolutionScaleFactor = 0.5;
                } else {
                    mvp = inputMVP;
                    resolutionScaleFactor = 1.0;
                }

                state.visible = state.visible || !bboxCulled;

                // if frustum culling is enabled, then do not consider culled shapes in the texture resolution determination
                if (this._viewFrustmCulling && bboxCulled) {
                    continue;
                }

                // COMPUTE RESOLUTION
                // if eye is inside box, use the highest resolution
                vec3.subtract(diffVec, eye, bbox.center);
                let dist = vec3.length(diffVec);
                if (dist < (bbox.diameter / 2.0)) {
                    // if we are inside bbox this is when one texture is mapped onto more shapes to get the maximum resolution
                    state.projectedBBoxResolution = this._textureResolutionLimit * resolutionScaleFactor; // use the maximum resolution
                    // set the maximal resolution in case that we are inside box
                    state.dist = 0;
                } else {
                    // find the closest box to the eye which uses current texture
                    if (dist < state.distance) {
                        state.distance = dist;
                    }

                    // compute distance between eye and intersection point on plane which is perpendicular to view direction and closest point on bounding box lies on that plane

                    // compute vector pointing to the right (1, 0, 0) in eye space
                    mat4.invert(inverseMvp, mvp);
                    let tmp = vec4.create();
                    vec4.transformMat4(tmp, right, inverseMvp);
                    let rightOsNorm = vec4.create();
                    vec4.normalize(rightOsNorm, tmp);

                    // compute two adjacent points laying on the plane at "dist" distance from the eye
                    let pointA = bbox.getClosestPoint(vec3.fromValues(eye[0], eye[1], eye[2]));
                    let pointB = vec3.create();
                    let scale = bbox.getDiameter();
                    vec3.scaleAndAdd(pointB, pointA, vec3.fromValues(rightOsNorm[0], rightOsNorm[1], rightOsNorm[2]), scale);

                    let winA = vec4.create();
                    let winB = vec4.create();
                    project(winA, pointA, mvp, [0, 0, vpSize[0], vpSize[1]]);
                    project(winB, pointB, mvp, [0, 0, vpSize[0], vpSize[1]]);

                    let currProjectedBboxSizeX = Math.abs(Math.abs(winA[0]) - Math.abs(winB[0]));
                    let currProjectedBboxSizeY = Math.abs(Math.abs(winA[1]) - Math.abs(winB[1]));

                    state.projectedBBoxResolution = Math.max(state.projectedBBoxResolution, currProjectedBboxSizeX * resolutionScaleFactor);
                    state.projectedBBoxResolution = Math.max(state.projectedBBoxResolution, currProjectedBboxSizeY * resolutionScaleFactor);
                }
            }

            this._computeLevel(state, textureDataHandler);
            computedStates.push(state);
        }

        computedStates.sort(function (queryA, queryB) {

            // if both queries priority are different from zero then the visibility has the highest weight
            if (queryA.visible && !queryB.visible) {
                return -1;
            }
            if (queryB.visible && !queryA.visible) {
                return 1;
            }

            // finally the distance take its place in the priority weight determination
            if (queryA.distance < queryB.distance) {
                return -1;
            }
            if (queryB.distance < queryA.distance) {
                return 1;
            }

            return 0;
        });

        ctx.postMessage({type: 'recomputed', computedValues: computedStates});
    }

    public setViewFrustumCullingForTexturesSwitching(aMessage: any) {
        this._viewFrustmCulling = aMessage.value;
    }

    public limitTexturesResolution(aMessage: any) {
        this._textureResolutionLimit = aMessage.value;
    }

    public acceptMessage(e: MessageEvent) {
        let message = e.data;
        switch (message.type) {
            case 'resourceMessages':
                for (let i = 0; i != message.resourceMessages.length; i++) {
                    let resourceMessage = message.resourceMessages[i];

                    switch (resourceMessage.type) {
                        case 'addTextureDataHandler':
                            this.addTextureDataHandler(resourceMessage);
                            break;
                        case 'removeTextureDataHandler':
                            this.removeTextureDataHandler(resourceMessage);
                            break;
                        case 'attachShapeBBox':
                            this.attachShapeBBox(resourceMessage);
                            break;
                        case 'updateShapeBBox':
                            this.updateShapeBBox(resourceMessage);
                            break;
                        case 'detachShapeBBox':
                            this.detachShapeBBox(resourceMessage);
                            break;
                    }
                }
                this.scheduleRecomputeTextureLOD();
                break;
            case 'setViewFrustumCullingForTexturesSwitching':
                this.setViewFrustumCullingForTexturesSwitching(message);
                break;
            case 'limitTexturesResolution':
                this.limitTexturesResolution(message);
                break;
            case 'recomputeTextureLOD':
                this._currentRecomputeLODMessage = message;
                this.scheduleRecomputeTextureLOD();
                break;
        }
    }
}

let resolutionComputeWorker = new ResolutionComputeWorker();

ctx.addEventListener('message', function(e) {
    resolutionComputeWorker.acceptMessage(e);
}, false);
